import React from 'react';

// ---------------------------------------------------------------------------
// Time range keys
// ---------------------------------------------------------------------------
export type TimeRangeKey = '1d' | '7d' | '1m' | '6m' | '1y';

// ---------------------------------------------------------------------------
// Per-range graph bundle
// ---------------------------------------------------------------------------
export interface GraphBundle {
  labels: string[];
  dataset1: number[];
  dataset2: number[];
  dataset3?: number[];
}

// ---------------------------------------------------------------------------
// Per-group live state slices
// ---------------------------------------------------------------------------
export interface LiveStateG1 {
  registrations: number;
  ftds: number;
  m1Graphs: Record<TimeRangeKey, GraphBundle>;
}

export interface LiveStateG2 {
  grossRevenue: number;
  stakes: number;
  wins: number;
  m2Graphs: Record<TimeRangeKey, GraphBundle>;
  deposits: number;
  withdrawals: number;
  playerBalance: number;
  netRevenue: number;
  bonusCost: number;
  bonusGgrRatio: number;
}

export interface LiveStateG3 {
  activePlayers: number;
  activePlayersChange: number;
  avgBet: number;
  avgBetChange: number;
  liveRtp: number;
  liveRtpChange: number;
}

export interface LiveStateG4 {
  topGamesStakes: number[];
  topPlayersStakes: number[];
}

/** Full combined live state (kept for generateNextLiveState compat) */
export type LiveState = LiveStateG1 & LiveStateG2 & LiveStateG3 & LiveStateG4;

// ---------------------------------------------------------------------------
// Label sets per range
// ---------------------------------------------------------------------------
const LABELS: Record<TimeRangeKey, string[]> = {
  '1d': ['00:00', '02:00', '04:00', '06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'],
  '7d': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  '1m': ['W1', 'W2', 'W3', 'W4'],
  '6m': ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'],
  '1y': ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'],
};

// ---------------------------------------------------------------------------
// Helper: build initial GraphBundle
// ---------------------------------------------------------------------------
function makeBundle(range: TimeRangeKey, base1: number, base2: number, base3?: number): GraphBundle {
  const n = LABELS[range].length;
  const series = (base: number, spread: number) =>
    Array.from({ length: n }, (_, i) =>
      Math.max(1, Math.round(base * (0.6 + (i / (n - 1)) * 0.8) + (Math.random() - 0.5) * spread)),
    );
  return {
    labels: LABELS[range],
    dataset1: series(base1, base1 * 0.4),
    dataset2: series(base2, base2 * 0.4),
    ...(base3 !== undefined ? { dataset3: series(base3, base3 * 0.4) } : {}),
  };
}

// ---------------------------------------------------------------------------
// Scalar multipliers per range (relative to 7d baseline)
// ---------------------------------------------------------------------------
const SCALAR: Record<TimeRangeKey, number> = {
  '1d': 1 / 7,
  '7d': 1,
  '1m': 4,
  '6m': 24,
  '1y': 52,
};

// ---------------------------------------------------------------------------
// Group update intervals (ms)
// ---------------------------------------------------------------------------
export const GROUP_INTERVALS = {
  group1: 16000,
  group2: 12000,
  group3: 8000,
  group4: 4000,
} as const;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------
export const initialLiveStateG1: LiveStateG1 = {
  registrations: 200,
  ftds: 124,
  m1Graphs: {
    '1d': makeBundle('1d', 29, 18),
    '7d': { labels: LABELS['7d'], dataset1: [30, 45, 28, 80, 99, 70, 50], dataset2: [20, 35, 18, 45, 60, 33, 40] },
    '1m': makeBundle('1m', 200, 124),
    '6m': makeBundle('6m', 800, 500),
    '1y': makeBundle('1y', 1600, 1000),
  },
};

export const initialLiveStateG2: LiveStateG2 = {
  grossRevenue: 84500,
  stakes: 12000,
  wins: 42000,
  m2Graphs: {
    '1d': makeBundle('1d', 12000, 1700, 600),
    '7d': {
      labels: LABELS['7d'],
      dataset1: [3000, 5000, 7000, 8000, 9000, 10500, 12000],
      dataset2: [2000, 3000, 4000, 5000, 6000, 7000, 8000],
      dataset3: [1000, 1500, 2000, 2500, 3000, 3500, 4000],
    },
    '1m': makeBundle('1m', 84500, 12000, 42000),
    '6m': makeBundle('6m', 338000, 48000, 168000),
    '1y': makeBundle('1y', 676000, 96000, 336000),
  },
  deposits: 169000,
  withdrawals: 12000,
  playerBalance: 42000,
  netRevenue: 70500,
  bonusCost: 14000,
  bonusGgrRatio: 19.24,
};

export const initialLiveStateG3: LiveStateG3 = {
  activePlayers: 48000,
  activePlayersChange: 1200,
  avgBet: 12.5,
  avgBetChange: 5.7,
  liveRtp: 97.32,
  liveRtpChange: 0.15,
};

export const initialLiveStateG4: LiveStateG4 = {
  topGamesStakes: [2000, 1800, 1700, 1300, 1100],
  topPlayersStakes: [3200, 2800, 32000, 32000, 32000],
};

/** Combined initial state (used by legacy generateNextLiveState) */
export const initialLiveState: LiveState = {
  ...initialLiveStateG1,
  ...initialLiveStateG2,
  ...initialLiveStateG3,
  ...initialLiveStateG4,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fluctuate(value: number, maxPct = 0.05, decimals = 0): number {
  const delta = value * maxPct * (Math.random() * 2 - 1);
  return parseFloat((value + delta).toFixed(decimals));
}

function fluctuateArray(arr: number[], maxPct = 0.08): number[] {
  return arr.map((v) => Math.max(0, Math.round(fluctuate(v, maxPct))));
}

function fluctuateBundle(b: GraphBundle, maxPct = 0.08): GraphBundle {
  return {
    labels: b.labels,
    dataset1: fluctuateArray(b.dataset1, maxPct),
    dataset2: fluctuateArray(b.dataset2, maxPct),
    ...(b.dataset3 ? { dataset3: fluctuateArray(b.dataset3, maxPct) } : {}),
  };
}

function fluctuateAllBundles(
  bundles: Record<TimeRangeKey, GraphBundle>,
  maxPct = 0.08,
): Record<TimeRangeKey, GraphBundle> {
  return {
    '1d': fluctuateBundle(bundles['1d'], maxPct),
    '7d': fluctuateBundle(bundles['7d'], maxPct),
    '1m': fluctuateBundle(bundles['1m'], maxPct),
    '6m': fluctuateBundle(bundles['6m'], maxPct),
    '1y': fluctuateBundle(bundles['1y'], maxPct),
  };
}

function fmtCurrency(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}
function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
function fmtPct(n: number): string {
  return n.toFixed(2) + '%';
}
function pctChange(cur: number, prv: number): { label: string; type: 'increase' | 'decrease' | 'neutral' } {
  if (prv === 0) return { label: '0.00%', type: 'neutral' };
  const diff = ((cur - prv) / prv) * 100;
  return {
    label: `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%`,
    type: diff > 0 ? 'increase' : diff < 0 ? 'decrease' : 'neutral',
  };
}
function absChange(cur: number, prv: number): { label: string; type: 'increase' | 'decrease' | 'neutral' } {
  const diff = cur - prv;
  return {
    label: `${diff >= 0 ? '+' : ''}${fmtInt(Math.abs(diff))}`,
    type: diff > 0 ? 'increase' : diff < 0 ? 'decrease' : 'neutral',
  };
}

// ---------------------------------------------------------------------------
// Per-group next-state generators
// ---------------------------------------------------------------------------
export function generateNextLiveStateG1(prev: LiveStateG1): LiveStateG1 {
  return {
    registrations: fluctuate(prev.registrations, 0.06),
    ftds: fluctuate(prev.ftds, 0.07),
    m1Graphs: fluctuateAllBundles(prev.m1Graphs, 0.1),
  };
}

export function generateNextLiveStateG2(prev: LiveStateG2): LiveStateG2 {
  return {
    grossRevenue: fluctuate(prev.grossRevenue, 0.04),
    stakes: fluctuate(prev.stakes, 0.05),
    wins: fluctuate(prev.wins, 0.05),
    m2Graphs: fluctuateAllBundles(prev.m2Graphs, 0.08),
    deposits: fluctuate(prev.deposits, 0.04),
    withdrawals: fluctuate(prev.withdrawals, 0.06),
    playerBalance: fluctuate(prev.playerBalance, 0.03),
    netRevenue: fluctuate(prev.netRevenue, 0.04),
    bonusCost: fluctuate(prev.bonusCost, 0.05),
    bonusGgrRatio: fluctuate(prev.bonusGgrRatio, 0.03, 2),
  };
}

export function generateNextLiveStateG3(prev: LiveStateG3): LiveStateG3 {
  return {
    activePlayers: fluctuate(prev.activePlayers, 0.02),
    activePlayersChange: fluctuate(prev.activePlayersChange, 0.15),
    avgBet: fluctuate(prev.avgBet, 0.04, 2),
    avgBetChange: fluctuate(prev.avgBetChange, 0.1, 2),
    liveRtp: Math.min(99.99, Math.max(90, fluctuate(prev.liveRtp, 0.005, 2))),
    liveRtpChange: fluctuate(prev.liveRtpChange, 0.2, 2),
  };
}

export function generateNextLiveStateG4(prev: LiveStateG4): LiveStateG4 {
  return {
    topGamesStakes: fluctuateArray(prev.topGamesStakes, 0.08),
    topPlayersStakes: fluctuateArray(prev.topPlayersStakes, 0.06),
  };
}

/** Legacy combined generator */
export function generateNextLiveState(prev: LiveState): LiveState {
  return {
    ...generateNextLiveStateG1(prev),
    ...generateNextLiveStateG2(prev),
    ...generateNextLiveStateG3(prev),
    ...generateNextLiveStateG4(prev),
  };
}

// ---------------------------------------------------------------------------
// Name pools
// ---------------------------------------------------------------------------
const GAME_NAMES = [
  'Chicken Rush',
  'Diamond Crown',
  'Lucky Drops',
  'Royal Coins',
  'Mines & Treasures',
  'Golden Sphinx',
  'Neon Blaze',
  'Dragon Hoard',
  'Wild Safari',
  'Aztec Gold',
  'Cash Volcano',
  'Star Burst',
  'Fruit Frenzy',
  'Thunder Reels',
  'Pirate Plunder',
];

const PLAYER_NAMES = [
  'Dragos',
  'Codrut',
  'Alex',
  'Catalin',
  'Mihai',
  'Andrei',
  'Bogdan',
  'Cristian',
  'Florin',
  'George',
  'Ionut',
  'Marius',
  'Nicolae',
  'Razvan',
  'Stefan',
];

function pickUnique<T>(pool: T[], n: number): T[] {
  const copy = [...pool];
  const result: T[] = [];
  for (let i = 0; i < n && copy.length; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    result.push(copy.splice(idx, 1)[0]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// getMockData — returns data.groups
// ---------------------------------------------------------------------------
export function getMockData(
  timeRange: Record<string, string>[],
  onTimeRangeChange: React.MouseEventHandler<HTMLElement>,
  selectedTimeRange: Record<string, string>,
  g1Live: LiveStateG1 = initialLiveStateG1,
  g1Prev: LiveStateG1 = initialLiveStateG1,
  g2Live: LiveStateG2 = initialLiveStateG2,
  g2Prev: LiveStateG2 = initialLiveStateG2,
  g3Live: LiveStateG3 = initialLiveStateG3,
  g3Prev: LiveStateG3 = initialLiveStateG3,
  g4Live: LiveStateG4 = initialLiveStateG4,
  g4Prev: LiveStateG4 = initialLiveStateG4,
) {
  const r1 = (selectedTimeRange.metrics1 ?? '7d') as TimeRangeKey;
  const r2 = (selectedTimeRange.metrics2 ?? '7d') as TimeRangeKey;
  const r3 = (selectedTimeRange.metrics3 ?? '7d') as TimeRangeKey;
  const r4 = (selectedTimeRange.metrics4 ?? '7d') as TimeRangeKey;

  const scale = (base: number, range: TimeRangeKey) => base * SCALAR[range];

  // Group 1 – New Players
  const regs1 = Math.round(scale(g1Live.registrations, r1));
  const ftds1 = Math.round(scale(g1Live.ftds, r1));
  const pregs1 = Math.round(scale(g1Prev.registrations, r1));
  const pftds1 = Math.round(scale(g1Prev.ftds, r1));
  const m1g = g1Live.m1Graphs[r1];
  const regChange = pctChange(regs1, pregs1);
  const ftdChange = pctChange(ftds1, pftds1);

  // Group 2 – Financials
  const ggr2 = scale(g2Live.grossRevenue, r2);
  const stk2 = scale(g2Live.stakes, r2);
  const win2 = scale(g2Live.wins, r2);
  const pggr2 = scale(g2Prev.grossRevenue, r2);
  const dep3 = scale(g2Live.deposits, r3);
  const wdl3 = scale(g2Live.withdrawals, r3);
  const bal3 = scale(g2Live.playerBalance, r3);
  const pdep3 = scale(g2Prev.deposits, r3);
  const rev4 = scale(g2Live.netRevenue, r4);
  const bon4 = scale(g2Live.bonusCost, r4);
  const prev4 = scale(g2Prev.netRevenue, r4);
  const m2g = g2Live.m2Graphs[r2];
  const ggrChange = pctChange(ggr2, pggr2);
  const depositsChange = pctChange(dep3, pdep3);
  const netRevChange = pctChange(rev4, prev4);

  // Group 3 – Live KPIs
  const activeChange = absChange(g3Live.activePlayers, g3Prev.activePlayers);
  const avgBetChange = pctChange(g3Live.avgBet, g3Prev.avgBet);
  const rtpChange = pctChange(g3Live.liveRtp, g3Prev.liveRtp);

  const vsLabel: Record<TimeRangeKey, string> = {
    '1d': 'vs yesterday',
    '7d': 'vs same day last week',
    '1m': 'vs last month',
    '6m': 'vs last 6 months',
    '1y': 'vs last year',
  };

  return {
    data: {
      groups: [
        {
          id: 'group1',
          interval: GROUP_INTERVALS.group1,
          metrics: [
            {
              id: 'metrics1',
              size: 'size-12',
              type: 'with-graph-extended',
              title: 'New Players Joining Your Casino',
              subtitle: 'How many people are coming in and are they converting?',
              links: [
                {
                  name: 'Registration',
                  url: '#registrations',
                  value: fmtInt(regs1),
                  change: regChange.label,
                  changeType: regChange.type,
                  vs: vsLabel[r1],
                },
                {
                  name: 'FTDs',
                  url: '#ftds',
                  value: fmtInt(ftds1),
                  change: ftdChange.label,
                  changeType: ftdChange.type,
                  vs: vsLabel[r1],
                },
              ],
              timeRange,
              selectedTimeRange: r1,
              actions: { onTimeRangeChange },
              graph: {
                type: 'line',
                data: {
                  labels: m1g.labels,
                  datasets: [
                    {
                      label: 'Registrations',
                      data: m1g.dataset1,
                      borderColor: '#803CFF',
                      backgroundColor: '#803CFF',
                      tension: 0.3,
                    },
                    {
                      label: 'FTDs',
                      data: m1g.dataset2,
                      borderColor: '#EC4899',
                      backgroundColor: '#EC4899',
                      tension: 0.3,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      align: 'end' as const,
                      labels: { boxWidth: 8, boxHeight: 8, useBorderRadius: true, borderRadius: 4 },
                    },
                  },
                },
              },
            },
          ],
        },
        {
          id: 'group2',
          title: 'Financial Performance',
          interval: GROUP_INTERVALS.group2,
          metrics: [
            {
              id: 'metrics2',
              size: 'size-12',
              type: 'with-graph-extended',
              title: 'Financial Performance',
              subtitle: 'Tracking the conversion from Handle to Gross Profit',
              links: [
                {
                  name: 'Gross Revenue',
                  url: '#ggr',
                  value: fmtCurrency(ggr2),
                  change: ggrChange.label,
                  changeType: ggrChange.type,
                  vs: vsLabel[r2],
                },
              ],
              stats: [
                { name: 'Stakes', value: fmtCurrency(stk2) },
                { name: 'Wins', value: fmtCurrency(win2) },
              ],
              timeRange,
              selectedTimeRange: r2,
              actions: { onTimeRangeChange },
              graph: {
                type: 'line',
                data: {
                  labels: m2g.labels,
                  datasets: [
                    {
                      label: 'GGR',
                      data: m2g.dataset1,
                      borderColor: '#8070E9',
                      backgroundColor: '#8070E9',
                      tension: 0.3,
                    },
                    {
                      label: 'Stakes',
                      data: m2g.dataset2,
                      borderColor: '#335C49',
                      backgroundColor: '#335C49',
                      tension: 0.3,
                    },
                    {
                      label: 'Wins',
                      data: m2g.dataset3!,
                      borderColor: '#0052AA',
                      backgroundColor: '#0052AA',
                      tension: 0.3,
                      borderDash: [3, 3],
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      align: 'end' as const,
                      labels: { boxWidth: 8, boxHeight: 8, useBorderRadius: true, borderRadius: 4 },
                    },
                  },
                },
              },
            },
            {
              id: 'metrics3',
              size: 'size-6 size-md-12',
              type: 'no-graph',
              title: 'Money Flow',
              subtitle: 'The movement of real cash',
              links: [
                {
                  name: 'Deposits',
                  url: '#deposits',
                  value: fmtCurrency(dep3),
                  change: depositsChange.label,
                  changeType: depositsChange.type,
                  vs: vsLabel[r3],
                },
              ],
              stats: [
                { name: 'Withdrawals', value: fmtCurrency(wdl3) },
                { name: 'Player Balance', value: fmtCurrency(bal3) },
              ],
              timeRange,
              selectedTimeRange: r3,
              actions: { onTimeRangeChange },
            },
            {
              id: 'metrics4',
              size: 'size-6 size-md-12',
              type: 'no-graph',
              title: 'Earnings',
              subtitle: 'The movement of real cash',
              links: [
                {
                  name: 'Net Revenue',
                  url: '#revenue',
                  value: fmtCurrency(rev4),
                  change: netRevChange.label,
                  changeType: netRevChange.type,
                  vs: vsLabel[r4],
                },
              ],
              stats: [
                { name: 'Bonus cost', value: fmtCurrency(bon4) },
                { name: 'Bonus/GGR Ratio', value: fmtPct(g2Live.bonusGgrRatio) },
              ],
              timeRange,
              selectedTimeRange: r4,
              actions: { onTimeRangeChange },
            },
          ],
        },
        {
          id: 'group3',
          title: 'Operational Performance',
          interval: GROUP_INTERVALS.group3,
          metrics: [
            {
              id: 'metrics5',
              size: 'size-4 size-md-6',
              type: 'simple',
              title: '',
              subtitle: '',
              links: [
                {
                  name: 'Active Players',
                  url: '',
                  value: fmtInt(g3Live.activePlayers),
                  change: activeChange.label,
                  changeType: activeChange.type,
                  vs: 'from yesterday',
                },
              ],
              stats: [],
              timeRange: [],
              selectedTimeRange: selectedTimeRange.metrics5,
              actions: {},
            },
            {
              id: 'metrics6',
              size: 'size-4 size-md-6',
              type: 'simple',
              title: '',
              subtitle: '',
              links: [
                {
                  name: 'Player Average Bet',
                  url: '',
                  value: '$' + g3Live.avgBet.toFixed(2),
                  change: avgBetChange.label,
                  changeType: avgBetChange.type,
                  vs: 'vs last month',
                },
              ],
              stats: [],
              timeRange: [],
              selectedTimeRange: selectedTimeRange.metrics6,
              actions: {},
            },
            {
              id: 'metrics7',
              size: 'size-4 size-md-12',
              type: 'simple',
              title: '',
              subtitle: '',
              links: [
                {
                  name: 'Live RTP',
                  url: '',
                  value: fmtPct(g3Live.liveRtp),
                  change: rtpChange.label,
                  changeType: rtpChange.type,
                  vs: 'from yesterday',
                },
              ],
              stats: [],
              timeRange: [],
              selectedTimeRange: selectedTimeRange.metrics7,
              actions: {},
            },
          ],
        },
        {
          id: 'group4',
          title: 'Spotlight',
          interval: GROUP_INTERVALS.group4,
          metrics: [
            {
              id: 'metrics8',
              size: 'size-6 size-md-12',
              type: 'top-table',
              title: '',
              subtitle: '',
              data: {
                labels: { primary: 'Top Games', secondary: 'Stakes' },
                rows: (() => {
                  const sorted = [...g4Live.topGamesStakes].sort((a, b) => b - a);
                  const names = pickUnique(GAME_NAMES, sorted.length);
                  return sorted.map((v, i) => ({ name: names[i], value: fmtCurrency(v) }));
                })(),
              },
            },
            {
              id: 'metrics9',
              size: 'size-6 size-md-12',
              type: 'top-table',
              title: '',
              subtitle: '',
              data: {
                labels: { primary: 'Top Players', secondary: 'Stakes' },
                rows: (() => {
                  const sorted = [...g4Live.topPlayersStakes].sort((a, b) => b - a);
                  const names = pickUnique(PLAYER_NAMES, sorted.length);
                  return sorted.map((v, i) => ({ name: names[i], value: fmtCurrency(v) }));
                })(),
              },
            },
          ],
        },
      ],
    },
  };
}
